import { useEffect, useRef } from "react";
import { thSwitch, thPick, thLevel, thCrash, thStart } from "@/lib/twoHeartsSfx";

const PER_LEVEL = 15;
const mulberry = (a) => () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
const COLORS = [["#ff5c93", "#ff9cc0"], ["#f6b73c", "#ffe08a"]];

const heartPath = (c, x, y, s) => {
  c.beginPath();
  c.moveTo(x, y + s * 0.35);
  c.bezierCurveTo(x - s * 1.1, y - s * 0.35, x - s * 0.55, y - s * 1.05, x, y - s * 0.5);
  c.bezierCurveTo(x + s * 0.55, y - s * 1.05, x + s * 1.1, y - s * 0.35, x, y + s * 0.35);
  c.closePath();
};
const thornPath = (c, x, y, s) => {
  c.beginPath();
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2, r = i % 2 ? s * 0.45 : s;
    c.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
  }
  c.closePath();
};

function makeState() {
  return { lanes: [0, 1], hx: [null, null], objs: [], parts: [], spawn: [1.4, 1.9], score: 0, pearls: 0, level: 1, combo: 0,
    phase: "run", meetT: 0, t: 0, over: false, shake: 0, stars: Array.from({ length: 40 }, () => ({ x: Math.random(), y: Math.random(), s: Math.random() })) };
}

// "2 Cars"-style engine: each screen half steers one heart between two lanes
export function useTwoHearts(canvasRef, { demo = false, onOver, onScore, heartY = 0.8, seed, skin } = {}) {
  const skinKey = JSON.stringify(skin || null);
  const st = useRef(makeState());
  const cb = useRef({ onOver, onScore });
  cb.current = { onOver, onScore };

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return undefined;
    const c = cv.getContext("2d");
    let raf, last = performance.now(), W = 0, H = 0, dpr = 1;
    const fit = () => { dpr = Math.min(window.devicePixelRatio || 1, 2); W = cv.clientWidth; H = cv.clientHeight; cv.width = W * dpr; cv.height = H * dpr; };
    fit();
    window.addEventListener("resize", fit);
    st.current = makeState();
    const rnd = seed ? mulberry(seed) : Math.random;
    const HC = skin?.heart ? [[skin.heart[0], skin.heart[0]], [skin.heart[1], skin.heart[1]]] : COLORS;
    const BG = skin?.bg || ["#2a0b2e", "#5a1240"];
    if (!demo) thStart();
    const laneX = (side, lane) => (side * W) / 2 + (W / 2) * (lane ? 0.72 : 0.28);
    const size = () => Math.min(W * 0.085, 30);

    const toggle = (side) => {
      const s = st.current;
      if (s.over || s.phase !== "run") return;
      s.lanes[side] ^= 1; if (!demo) thSwitch();
    };
    const down = (e) => { if (demo) return; e.preventDefault(); const r = cv.getBoundingClientRect(); toggle(e.clientX - r.left < r.width / 2 ? 0 : 1); };
    const key = (e) => { if (demo) return; if (["ArrowLeft", "a", "A"].includes(e.key)) toggle(0); if (["ArrowRight", "d", "D"].includes(e.key)) toggle(1); };
    cv.addEventListener("pointerdown", down);
    window.addEventListener("keydown", key);

    const burst = (x, y, col, n = 10) => { for (let i = 0; i < n; i++) st.current.parts.push({ x, y, vx: (Math.random() - 0.5) * 260, vy: (Math.random() - 0.9) * 260, life: 0.7, col }); };

    const ai = (s, hy) => {
      [0, 1].forEach((side) => {
        const ahead = s.objs.filter((o) => o.side === side && o.y < hy && o.y > hy - H * 0.45).sort((a, b) => b.y - a.y)[0];
        if (!ahead) return;
        const cur = s.lanes[side];
        if ((ahead.type === "thorn" && ahead.lane === cur) || (ahead.type === "pearl" && ahead.lane !== cur)) s.lanes[side] ^= 1;
      });
    };

    const step = (dt) => {
      const s = st.current, hy = H * heartY, r = size();
      s.t += dt;
      s.stars.forEach((p) => { p.y += dt * (0.02 + p.s * 0.05) * (1 + s.level * 0.15); if (p.y > 1) { p.y = 0; p.x = Math.random(); } });
      [0, 1].forEach((side) => {
        const tx = s.phase === "meet" ? W / 2 + (side ? 1 : -1) * r * 0.6 * (1 - Math.min(1, s.meetT / 0.9)) : laneX(side, s.lanes[side]);
        s.hx[side] = s.hx[side] == null ? tx : s.hx[side] + (tx - s.hx[side]) * Math.min(1, dt * 16);
      });
      if (s.over) { s.shake = Math.max(0, s.shake - dt * 30); s.parts.forEach((p) => { p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 500 * dt; p.life -= dt; }); s.parts = s.parts.filter((p) => p.life > 0); return; }
      if (s.phase === "meet") {
        s.meetT += dt;
        if (s.meetT > 1.6) { s.phase = "run"; s.meetT = 0; }
        return;
      }
      if (demo) ai(s, hy);
      const v = H * (0.38 + 0.055 * (s.level - 1));
      const gap = Math.max(0.42, 0.9 - 0.045 * (s.level - 1));
      [0, 1].forEach((side) => {
        s.spawn[side] -= dt;
        if (s.spawn[side] <= 0) {
          s.spawn[side] = gap * (0.85 + rnd() * 0.4);
          s.objs.push({ side, lane: rnd() < 0.5 ? 0 : 1, y: -r * 2, type: rnd() < 0.42 + Math.min(0.12, s.level * 0.015) ? "thorn" : "pearl" });
        }
      });
      for (const o of s.objs) {
        o.y += v * dt;
        if (o.done || o.side === undefined) continue;
        const hx = s.hx[o.side];
        if (Math.abs(o.y - hy) < r * 1.05 && Math.abs(laneX(o.side, o.lane) - hx) < r * 1.2) {
          o.done = true;
          if (o.type === "pearl") {
            s.pearls += 1; s.combo += 1; s.score += 1 + Math.floor(s.combo / 10);
            burst(hx, hy, HC[o.side][1], 8); if (!demo) thPick(s.combo);
            cb.current.onScore?.(s.score);
            if (s.pearls % PER_LEVEL === 0) {
              s.level += 1; s.score += s.level * 2; s.phase = "meet"; s.meetT = 0; s.objs = [];
              if (!demo) { thLevel(); navigator.vibrate?.(30); }
              break;
            }
          } else if (!demo) {
            s.over = true; s.shake = 14; burst(hx, hy, "#ff2d55", 26); burst(hx, hy, "#ffffff", 10); thCrash(); navigator.vibrate?.([60, 40, 120]);
            setTimeout(() => cb.current.onOver?.({ score: s.score, level: s.level }), 900);
          }
        } else if (o.type === "pearl" && o.y > hy + r * 1.5 && !o.missed) { o.missed = true; s.combo = 0; }
      }
      s.objs = s.objs.filter((o) => o.y < H + r * 3 && !(o.done && o.type === "pearl"));
      s.parts.forEach((p) => { p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 500 * dt; p.life -= dt; });
      s.parts = s.parts.filter((p) => p.life > 0);
    };

    const draw = () => {
      const s = st.current, r = size(), hy = H * heartY;
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      const g = c.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, BG[1]); g.addColorStop(1, BG[0]);
      c.fillStyle = g; c.fillRect(0, 0, W, H);
      if (s.shake) c.translate((Math.random() - 0.5) * s.shake, (Math.random() - 0.5) * s.shake);
      s.stars.forEach((p) => { c.globalAlpha = 0.25 + p.s * 0.5; c.fillStyle = "#ffd6e8"; c.fillRect(p.x * W, p.y * H, 1 + p.s * 1.5, 1 + p.s * 1.5); });
      c.globalAlpha = 1;
      [0, 1].forEach((side) => [0, 1].forEach((lane) => { c.fillStyle = side ? "rgba(246,183,60,0.06)" : "rgba(255,92,147,0.06)"; c.fillRect(laneX(side, lane) - r * 1.3, 0, r * 2.6, H); }));
      c.strokeStyle = "rgba(255,255,255,0.18)"; c.lineWidth = 2; c.setLineDash([14, 14]); c.lineDashOffset = -s.t * 120;
      c.beginPath(); c.moveTo(W / 2, 0); c.lineTo(W / 2, H); c.stroke(); c.setLineDash([]);
      for (const o of s.objs) {
        const x = laneX(o.side, o.lane);
        if (o.type === "pearl") {
          c.shadowColor = HC[o.side][0]; c.shadowBlur = 16; c.fillStyle = HC[o.side][1];
          heartPath(c, x, o.y, r * 0.55); c.fill(); c.shadowBlur = 0;
        } else {
          c.save(); c.translate(x, o.y); c.rotate(s.t * 2 + o.lane); c.fillStyle = "#1a0510"; c.strokeStyle = "#a3125a"; c.lineWidth = 2.5;
          thornPath(c, 0, 0, r * 0.78); c.fill(); c.stroke(); c.restore();
        }
      }
      const merge = s.phase === "meet" ? Math.min(1, s.meetT / 0.9) : 0;
      if (!s.over || s.shake > 6) {
        [0, 1].forEach((side) => {
          const pulse = 1 + Math.sin(s.t * 8 + side) * 0.05;
          c.shadowColor = HC[side][0]; c.shadowBlur = 24; c.fillStyle = HC[side][0];
          heartPath(c, s.hx[side] ?? laneX(side, side), hy, r * pulse); c.fill();
          c.shadowBlur = 0; c.fillStyle = "rgba(255,255,255,0.55)"; c.beginPath(); c.arc((s.hx[side] ?? 0) - r * 0.32, hy - r * 0.45, r * 0.14, 0, Math.PI * 2); c.fill();
        });
      }
      if (merge > 0.8) {
        const k = (merge - 0.8) / 0.2, rr = r * (2.2 + Math.sin(s.meetT * 6) * 0.2);
        c.globalAlpha = Math.min(1, k * 1.2); c.shadowColor = "#ff5c93"; c.shadowBlur = 40;
        const gg = c.createLinearGradient(W / 2 - rr, hy - rr, W / 2 + rr, hy + rr); gg.addColorStop(0, "#ff5c93"); gg.addColorStop(1, "#f6b73c");
        c.fillStyle = gg; heartPath(c, W / 2, hy, rr); c.fill(); c.shadowBlur = 0; c.globalAlpha = 1;
      }
      if (s.phase === "meet") {
        c.fillStyle = "#fff"; c.textAlign = "center"; c.font = "900 34px Nunito, sans-serif";
        c.globalAlpha = Math.min(1, s.meetT * 2); c.fillText(`Level ${s.level}`, W / 2, H * 0.4);
        c.font = "700 15px Nunito, sans-serif"; c.fillText("Eure Herzen haben sich gefunden 💞", W / 2, H * 0.4 + 28); c.globalAlpha = 1;
      }
      s.parts.forEach((p) => { c.globalAlpha = Math.max(0, p.life / 0.7); c.fillStyle = p.col; c.beginPath(); c.arc(p.x, p.y, 3, 0, Math.PI * 2); c.fill(); });
      c.globalAlpha = 1;
      if (!demo) {
        c.fillStyle = "#fff"; c.textAlign = "center"; c.font = "900 40px Nunito, sans-serif"; c.fillText(String(s.score), W / 2, 64);
        c.font = "800 12px Nunito, sans-serif"; c.fillStyle = "rgba(255,255,255,0.7)";
        c.fillText(`LEVEL ${s.level} · ${PER_LEVEL - (s.pearls % PER_LEVEL)} Herzen bis zum Treffpunkt`, W / 2, 86);
        if (s.t < 3.5) {
          c.globalAlpha = Math.max(0, 1 - (s.t - 2.5)); c.font = "800 14px Nunito, sans-serif"; c.fillStyle = "#fff";
          c.fillText("◀ links tippen", W / 4, H * 0.93); c.fillText("rechts tippen ▶", (W * 3) / 4, H * 0.93); c.globalAlpha = 1;
        }
      }
    };

    const loop = (now) => { const dt = Math.min(0.05, (now - last) / 1000); last = now; step(dt); draw(); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", fit); window.removeEventListener("keydown", key); cv.removeEventListener("pointerdown", down); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef, demo, heartY, seed, skinKey]);
}

export function TwoHeartsCanvas({ demo, onOver, onScore, testId, heartY, seed, skin }) {
  const ref = useRef(null);
  useTwoHearts(ref, { demo, onOver, onScore, heartY, seed, skin });
  return <canvas ref={ref} className="block w-full h-full touch-none select-none" data-testid={testId || "twohearts-canvas"} />;
}
